// Dunno why eslint doesn't like triple slash references?
// In any case you can't import ambient modules (e.g. ones that can declare modules rather than augment them)
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./vite-url.d.ts" />

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { domToBlob } from "modern-screenshot";
import modernScreenshotWorkerUrl from "modern-screenshot/worker?url";
import { useCallback, useEffect, useMemo, type RefObject } from "react";

import { useAssetIsResolving } from "@skilitics/assets";

import { useContentHostId, useThumbnailsApi } from "../../backend/index.ts";
import { useNodePickingActive } from "../../composable/index.ts";
import {
  nodeLifecycleEventName,
  nodeLifecycleEventTypes,
  subscribeEvent,
  unsubscribeEvent,
} from "../../events/index.ts";
import { LanguageEventTypes, useActiveLanguageCode } from "../../l10n/index.ts";
import { base64Encode } from "./helpers.ts";
import { getScreenshotLanguageCodeHashQueryKey, getScreenshotLanguageCodeQueryKey } from "./queryKeys.ts";

const imageType = "image/webp";

// this is the current activity thumbnail size - should make a parameter?
const thumbnailWidth = 220;
const thumbnailHeight = 140;

interface Options {
  nodeId: number;
  elementRef: RefObject<HTMLElement | null>;
  scaleFactor?: number;
}

const useScreenshotHash = ({ nodeId }: { nodeId: number }) => {
  const hostId = useContentHostId();
  const api = useThumbnailsApi();

  const languageCode = useActiveLanguageCode();

  const queryKey = getScreenshotLanguageCodeHashQueryKey(hostId, nodeId, languageCode);

  const { data = "", status } = useQuery({
    queryKey,
    queryFn: async () => {
      const hash = await api.getThumbnailHash({ hostId, nodeId, languageCode });
      return hash ?? ""; // must return "" for react query
    },
  });

  return { hash: data, queryKey, status };
};

const useSaveScreenshot = ({ nodeId }: { nodeId: number }): ((blob: Blob) => Promise<void>) => {
  const queryClient = useQueryClient();
  const hostId = useContentHostId();
  const api = useThumbnailsApi();

  const languageCode = useActiveLanguageCode();

  const { hash: existingHash, queryKey: hashQueryKey } = useScreenshotHash({ nodeId });
  const queryKey = getScreenshotLanguageCodeQueryKey(hostId, nodeId, languageCode);

  const { mutate } = useMutation<void, Error, { hash: string; objectUrl: string; file: File }>({
    mutationFn: async ({ file }) => {
      return await api.addThumbnail({ hostId, nodeId, languageCode, file });
    },
    onMutate: ({ hash, objectUrl }) => {
      // optimistic update so shows up immediately
      queryClient.setQueryData<string>(hashQueryKey, hash);
      queryClient.setQueryData<string>(queryKey, objectUrl);
    },
    onError: async (_err, _variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey, refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: hashQueryKey, refetchType: "all" }),
      ]);
    },
  });

  return useMemo(
    () => async (blob: Blob) => {
      // TODO: dededupe this from the useNautilusApi side
      // Use the same hash computation as the API to ensure consistency
      const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer()));
      // Note: We still use our base64Encode for the local hash since that's what the UI expects
      // but the API will compute its own hash using Base64.URL_SAFE.encode
      const screenshotHash = base64Encode(hash);

      // Only skip if we have a valid existing hash and it matches
      if (existingHash && existingHash === screenshotHash) {
        return;
      }

      const file = new File([blob], "screenshot", { type: imageType });
      const objectUrl = URL.createObjectURL(blob);

      mutate({ hash: screenshotHash, objectUrl, file });
    },
    [existingHash, mutate],
  );
};

const SCREENSHOT_TIMEOUT_PERIOD = 5000;

type Timer = ReturnType<typeof setTimeout>;

class ScreenshotManager {
  private queueTimer: Timer | undefined;
  private elementRef: RefObject<HTMLElement | null>;
  private scaleFactor: number;
  private saveScreenshot: (blob: Blob) => Promise<void>;
  private isStable: () => boolean;

  constructor(
    elementRef: RefObject<HTMLElement | null>,
    scaleFactor: number,
    saveScreenshot: (blob: Blob) => Promise<void>,
    isStable: () => boolean,
  ) {
    this.elementRef = elementRef;
    this.scaleFactor = scaleFactor;
    this.saveScreenshot = saveScreenshot;
    this.isStable = isStable;
  }

  private async takeScreenshot(): Promise<Blob | null> {
    // Double-check stability before capturing
    if (!this.isStable()) {
      return null;
    }

    const element = this.elementRef.current;
    if (!element) {
      // ignore when activity is unmounted and exited during screenshot.
      return null;
    }

    // todo: share code with useDefaultNodeSnapshotHandler() somehow

    // Render to the element's size within it's parent to avoid being affected by the canvas zoom.
    const width = element.clientWidth;
    const height = element.clientHeight;

    // scale the output image to match to the thumbnail size by default.
    // scaleFactor can be used to increase the resolution for higher DPI.
    // (using devicePixelRatio would create 1:1 pixel mapping for the current display, but
    // this image may be used across different machines, so should be consistent.)
    const scale = Math.min(thumbnailWidth / width, thumbnailHeight / height) * this.scaleFactor;

    return await domToBlob(element, {
      workerUrl: modernScreenshotWorkerUrl,
      type: imageType,
      width,
      height,
      // webp quality can be really low and still look good.
      // This ends up around 5kb at 2x scaleFactor (the current value)
      quality: 0.2,
      scale,
    });
  }

  subscribe(): () => void {
    const tokens = [
      ...nodeLifecycleEventTypes.map((type) =>
        subscribeEvent(nodeLifecycleEventName(type), () => {
          this.queue();
        }),
      ),
      subscribeEvent(LanguageEventTypes.ACTIVE_LANGUAGE_CODE_CHANGED, () => {
        void this.captureImmediately();
      }),
    ];

    return () => {
      for (const token of tokens) {
        unsubscribeEvent(token);
      }
      this.cancelQueue();
    };
  }

  queue(): void {
    // Skip screenshot capture if thumbnails are disabled
    if (import.meta.env.REACT_APP_DISABLE_THUMBNAILS === "true") {
      return;
    }

    this.cancelQueue();

    this.queueTimer = setTimeout(() => {
      void this.captureImmediately();
    }, SCREENSHOT_TIMEOUT_PERIOD);
  }

  cancelQueue(): void {
    if (this.queueTimer) {
      clearTimeout(this.queueTimer);
      this.queueTimer = undefined;
    }
  }

  async captureImmediately(): Promise<void> {
    // Skip screenshot capture if thumbnails are disabled
    if (import.meta.env.REACT_APP_DISABLE_THUMBNAILS === "true") {
      return;
    }

    this.cancelQueue();

    // Defer the expensive screenshot operation to avoid blocking the UI
    await new Promise((resolve) => setTimeout(resolve, 0));

    const screenshot = await this.takeScreenshot();
    if (screenshot) {
      await this.saveScreenshot(screenshot);
    }
  }

  get isPending(): boolean {
    return !!this.queueTimer;
  }
}

export const useScreenshotCapture = ({ elementRef, nodeId, scaleFactor }: Options) => {
  const saveScreenshot = useSaveScreenshot({ nodeId });
  const assetResolving = useAssetIsResolving();
  const nodePickingActive = useNodePickingActive();

  // Create a stable function to check if we should capture
  const isStable = useCallback(() => !assetResolving && !nodePickingActive, [assetResolving, nodePickingActive]);

  const screenshotManager = useMemo(
    () => new ScreenshotManager(elementRef, scaleFactor || 1, saveScreenshot, isStable),
    [elementRef, scaleFactor, saveScreenshot, isStable],
  );

  // Subscribe to events and cleanup on unmount
  useEffect(() => {
    return screenshotManager.subscribe();
  }, [screenshotManager]);

  const handleCaptureScreenshot = useCallback(async () => {
    return screenshotManager.captureImmediately();
  }, [screenshotManager]);

  return useMemo(
    () => ({
      capturePending: screenshotManager.isPending,
      captureScreenshot: handleCaptureScreenshot,
    }),
    [screenshotManager.isPending, handleCaptureScreenshot],
  );
};
